import { useMemo, useState } from 'react'
import {
  Camera, Cloud, Database, LogIn, LogOut, Mountain, Plus, Route as RouteIcon, Search, Trash2, X,
} from 'lucide-react'
import { demoRoutePoints, demoSummits, demoTrips } from '../data/demoData'
import { isSupabaseConfigured, supabase } from '../lib/supabase'
import ExpeditionEditor from './ExpeditionEditor'
import PhotoManager from './PhotoManager'
import PeakSearchField from './PeakSearchField'
import { flagFromCountryCode } from '../lib/placeSearch'


const emptySummit = {
  trip_id: '',
  country_code: '',
  country_name: '',
  flag: '',
  peak_name: '',
  elevation_m: '',
  latitude: '',
  longitude: '',
  status: 'planned',
  summit_date: '',
  scenic_choice: false,
  difficulty: '',
  route_notes: '',
  hero_image_url: '',
  reel_url: '',
}


const ADMIN_TABS = [
  { id: 'expeditions', label: 'Expeditions', icon: RouteIcon, blurb: 'One screen per trip: dates, the peaks on it, and the itinerary.' },
  { id: 'summits', label: 'Summits', icon: Mountain, blurb: 'Every peak, including ones not yet attached to a trip.' },
  { id: 'photos', label: 'Photos', icon: Camera, blurb: 'Upload trip photos; GPS places them on the map.' },
]

function cleanOptional(value) {
  return value === '' ? null : value
}

export default function AdminPanel({
  open,
  onClose,
  trips,
  summits,
  routePoints,
  routeLegs,
  routeLegsReady,
  routeLegsError,
  photos,
  photosReady,
  photosError,
  session,
  cloudEmpty,
  refresh,
}) {
  const [email, setEmail] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)
  const [summitForm, setSummitForm] = useState(emptySummit)
  const [tab, setTab] = useState('expeditions')
  const [summitFilter, setSummitFilter] = useState('')

  const cloudTrips = useMemo(() => cloudEmpty ? [] : trips, [cloudEmpty, trips])

  const tripNameById = useMemo(
    () => new Map(cloudTrips.map((trip) => [String(trip.id), trip.name])),
    [cloudTrips],
  )

  const visibleSummits = useMemo(() => {
    const query = summitFilter.trim().toLowerCase()
    const list = query
      ? summits.filter((summit) => [summit.country_name, summit.peak_name, summit.country_code, summit.status]
        .some((value) => String(value || '').toLowerCase().includes(query)))
      : summits
    return [...list].sort((a, b) => String(a.country_name || '').localeCompare(String(b.country_name || '')))
  }, [summits, summitFilter])

  if (!open) return null

  async function sendMagicLink(event) {
    event.preventDefault()
    if (!email || !supabase) return
    setBusy(true)
    setNotice('')
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    })
    setBusy(false)
    setNotice(error ? error.message : 'Magic link sent. Open it on this device to sign in.')
  }

  async function signOut() {
    if (!supabase) return
    await supabase.auth.signOut()
    setNotice('Signed out.')
  }

  async function seedCloud() {
    if (!supabase || !session) return
    setBusy(true)
    setNotice('')

    try {
      const ownerId = session.user.id
      const tripRows = demoTrips.map(({ id: _id, ...trip }) => ({ ...trip, owner_id: ownerId }))
      const { data: insertedTrips, error: tripsError } = await supabase
        .from('trips')
        .upsert(tripRows, { onConflict: 'owner_id,slug' })
        .select()
      if (tripsError) throw tripsError

      const idMap = new Map()
      demoTrips.forEach((demoTrip) => {
        const cloudTrip = insertedTrips.find((trip) => trip.slug === demoTrip.slug)
        if (cloudTrip) idMap.set(demoTrip.id, cloudTrip.id)
      })

      const summitRows = demoSummits.map(({ id: _id, trip_id, ...summit }) => ({
        ...summit,
        owner_id: ownerId,
        trip_id: idMap.get(trip_id) || null,
      }))
      const { error: summitsError } = await supabase.from('summits').insert(summitRows)
      if (summitsError) throw summitsError

      const pointRows = demoRoutePoints.map(({ id: _id, trip_id, ...point }) => ({
        ...point,
        owner_id: ownerId,
        trip_id: idMap.get(trip_id),
      }))
      const { error: pointsError } = await supabase.from('route_points').insert(pointRows)
      if (pointsError) throw pointsError

      setNotice('Starter routes loaded into Supabase. This is now your synced cloud copy.')
      await refresh()
    } catch (error) {
      setNotice(error.message || 'Could not load starter data.')
    } finally {
      setBusy(false)
    }
  }


  async function addSummit(event) {
    event.preventDefault()
    if (!supabase) return
    setBusy(true)
    const payload = {
      ...summitForm,
      trip_id: cleanOptional(summitForm.trip_id),
      elevation_m: Number(summitForm.elevation_m),
      latitude: Number(summitForm.latitude),
      longitude: Number(summitForm.longitude),
      summit_date: cleanOptional(summitForm.summit_date),
      hero_image_url: cleanOptional(summitForm.hero_image_url),
      reel_url: cleanOptional(summitForm.reel_url),
    }
    const { error } = await supabase.from('summits').insert(payload)
    setBusy(false)
    setNotice(error ? error.message : 'Summit added.')
    if (!error) {
      setSummitForm(emptySummit)
      await refresh()
    }
  }


  async function updateSummitTrip(summit, tripId) {
    if (!supabase || busy) return
    setBusy(true)
    const { error } = await supabase
      .from('summits')
      .update({ trip_id: tripId || null })
      .eq('id', summit.id)
    setBusy(false)
    setNotice(error
      ? error.message
      : `${summit.peak_name} moved to ${tripId ? (tripNameById.get(String(tripId)) || 'the trip') : 'no trip'}.`)
    if (!error) await refresh()
  }

  function applySummitPeak(result) {
    setSummitForm((current) => ({
      ...current,
      peak_name: result.label,
      country_name: result.countryName || current.country_name,
      country_code: result.countryCode || current.country_code,
      flag: flagFromCountryCode(result.countryCode) || current.flag,
      elevation_m: result.elevation ? String(result.elevation) : current.elevation_m,
      latitude: result.latitude.toFixed(6),
      longitude: result.longitude.toFixed(6),
    }))
  }


  async function updateSummitStatus(summit, status) {
    if (!supabase || busy || status === summit.status) return
    setBusy(true)
    setNotice(`Updating ${summit.peak_name}…`)
    const { error } = await supabase.from('summits').update({ status }).eq('id', summit.id)
    setBusy(false)
    setNotice(error ? error.message : `${summit.peak_name} marked ${status}.`)
    if (!error) await refresh()
  }

  async function removeRow(table, id, label) {
    if (!supabase || !window.confirm(`Delete ${label}?`)) return
    setBusy(true)
    const { error } = await supabase.from(table).delete().eq('id', id)
    setBusy(false)
    setNotice(error ? error.message : `${label} deleted.`)
    if (!error) await refresh()
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <section className="admin-panel" role="dialog" aria-modal="true" aria-label="Manage Summit Hub">
        <header className="admin-panel__header">
          <div>
            <div className="eyebrow">Private controls</div>
            <h2>Manage Summit Hub</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close admin panel"><X size={19} /></button>
        </header>

        {!isSupabaseConfigured && (
          <div className="setup-card">
            <Cloud size={22} />
            <div>
              <h3>Connect cloud syncing</h3>
              <p>The app is running in demo mode. Create a Supabase project, run <code>supabase/schema.sql</code>, then copy <code>.env.example</code> to <code>.env.local</code> and add the two project values.</p>
              <pre>VITE_SUPABASE_URL=...{`\n`}VITE_SUPABASE_PUBLISHABLE_KEY=...</pre>
            </div>
          </div>
        )}

        {isSupabaseConfigured && !session && (
          <form className="login-card" onSubmit={sendMagicLink}>
            <LogIn size={22} />
            <div>
              <h3>Admin sign-in</h3>
              <p>A magic link keeps the public page open to everyone while editing stays private.</p>
              <div className="inline-form">
                <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" required />
                <button className="primary-button" disabled={busy}>Send link</button>
              </div>
            </div>
          </form>
        )}

        {isSupabaseConfigured && session && (
          <>
            <div className="admin-session-bar">
              <span><Database size={15} /> Signed in as {session.user.email}</span>
              <button className="text-button" onClick={signOut}><LogOut size={15} /> Sign out</button>
            </div>

            {cloudEmpty && (
              <div className="seed-card">
                <div>
                  <strong>Your cloud database is empty.</strong>
                  <p>Load the current Summit Hub starter routes once. After that, every change is saved and synced automatically.</p>
                </div>
                <button className="primary-button" onClick={seedCloud} disabled={busy}>Load starter data</button>
              </div>
            )}

            {!cloudEmpty && (
              <>
                <nav className="admin-tabs" aria-label="Admin sections">
                  {ADMIN_TABS.map((item) => {
                    const Icon = item.icon
                    return (
                      <button
                        key={item.id}
                        className={tab === item.id ? 'active' : ''}
                        onClick={() => setTab(item.id)}
                        aria-current={tab === item.id}
                      >
                        <Icon size={14} /> <span>{item.label}</span>
                      </button>
                    )
                  })}
                </nav>
                <p className="admin-tab-blurb">{ADMIN_TABS.find((item) => item.id === tab)?.blurb}</p>

                {tab === 'summits' && (
                  <div className="admin-grid">
                    <form className="admin-form" onSubmit={addSummit}>
                      <div className="form-title"><Plus size={17} /> Add summit</div>
                      <PeakSearchField
                        onChoose={applySummitPeak}
                        hint="Fills the peak, country, flag, coordinates and elevation. Everything stays editable."
                      />
                      <label>Trip<select value={summitForm.trip_id} onChange={(event) => setSummitForm({ ...summitForm, trip_id: event.target.value })}><option value="">No trip</option>{cloudTrips.map((trip) => <option key={trip.id} value={trip.id}>{trip.name}</option>)}</select></label>
                      <div className="form-row"><label>Country<input value={summitForm.country_name} onChange={(event) => setSummitForm({ ...summitForm, country_name: event.target.value })} required /></label><label>Flag<input value={summitForm.flag} onChange={(event) => setSummitForm({ ...summitForm, flag: event.target.value })} placeholder="🇪🇸" /></label></div>
                      <div className="form-row"><label>Code<input value={summitForm.country_code} onChange={(event) => setSummitForm({ ...summitForm, country_code: event.target.value.toUpperCase() })} maxLength="2" /></label><label>Peak<input value={summitForm.peak_name} onChange={(event) => setSummitForm({ ...summitForm, peak_name: event.target.value })} required /></label></div>
                      <div className="form-row"><label>Elevation (m)<input type="number" value={summitForm.elevation_m} onChange={(event) => setSummitForm({ ...summitForm, elevation_m: event.target.value })} required /></label><label>Status<select value={summitForm.status} onChange={(event) => setSummitForm({ ...summitForm, status: event.target.value })}><option value="completed">Completed</option><option value="planned">Planned</option><option value="future">Future</option></select></label></div>
                      <div className="form-row"><label>Latitude<input type="number" step="any" value={summitForm.latitude} onChange={(event) => setSummitForm({ ...summitForm, latitude: event.target.value })} required /></label><label>Longitude<input type="number" step="any" value={summitForm.longitude} onChange={(event) => setSummitForm({ ...summitForm, longitude: event.target.value })} required /></label></div>
                      <div className="form-row"><label>Summit date<input type="date" value={summitForm.summit_date} onChange={(event) => setSummitForm({ ...summitForm, summit_date: event.target.value })} /></label><label>Difficulty<input value={summitForm.difficulty} onChange={(event) => setSummitForm({ ...summitForm, difficulty: event.target.value })} /></label></div>
                      <label className="checkbox-label"><input type="checkbox" checked={summitForm.scenic_choice} onChange={(event) => setSummitForm({ ...summitForm, scenic_choice: event.target.checked })} /> Iconic / scenic choice</label>
                      <label>Route notes<textarea value={summitForm.route_notes} onChange={(event) => setSummitForm({ ...summitForm, route_notes: event.target.value })} /></label>
                      <div className="form-row"><label>Photo URL<input type="url" value={summitForm.hero_image_url} onChange={(event) => setSummitForm({ ...summitForm, hero_image_url: event.target.value })} /></label><label>Reel URL<input type="url" value={summitForm.reel_url} onChange={(event) => setSummitForm({ ...summitForm, reel_url: event.target.value })} /></label></div>
                      <button className="primary-button" disabled={busy}>Save summit</button>
                    </form>
                    <div className="admin-list">
                      <div className="form-title admin-list__title">
                        <span>Current summits</span>
                        <small>{visibleSummits.length} of {summits.length}</small>
                      </div>
                      <div className="admin-filter">
                        <Search size={14} />
                        <input
                          value={summitFilter}
                          onChange={(event) => setSummitFilter(event.target.value)}
                          placeholder="Filter by country, peak or status"
                          aria-label="Filter summits"
                        />
                      </div>
                      {!visibleSummits.length && <div className="route-empty">No summits match that filter.</div>}
                      {visibleSummits.map((summit) => (
                        <div className="admin-list-row admin-summit-row" key={summit.id}>
                          <span>
                            <strong>{summit.flag} {summit.country_name}</strong>
                            <small>{summit.peak_name}{summit.trip_id ? ` · ${tripNameById.get(String(summit.trip_id)) || 'Trip'}` : ' · no trip'}</small>
                          </span>
                          <div className="admin-summit-actions">
                            <select
                              className="summit-status-select summit-trip-select"
                              value={summit.trip_id || ''}
                              onChange={(event) => updateSummitTrip(summit, event.target.value)}
                              disabled={busy}
                              aria-label={`Change the trip for ${summit.peak_name}`}
                            >
                              <option value="">No trip</option>
                              {cloudTrips.map((trip) => (
                                <option key={trip.id} value={trip.id}>{trip.name}</option>
                              ))}
                            </select>
                            <select
                              className="summit-status-select"
                              value={summit.status || 'future'}
                              onChange={(event) => updateSummitStatus(summit, event.target.value)}
                              disabled={busy}
                              aria-label={`Change ${summit.peak_name} status`}
                            >
                              <option value="completed">Completed</option>
                              <option value="planned">Planned</option>
                              <option value="future">Future</option>
                            </select>
                            <button className="icon-button icon-button--danger" onClick={() => removeRow('summits', summit.id, summit.peak_name)}><Trash2 size={15} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {tab === 'expeditions' && (
                  <ExpeditionEditor
                    trips={cloudTrips}
                    summits={summits}
                    routePoints={routePoints}
                    routeLegs={routeLegs}
                    routeLegsReady={routeLegsReady}
                    routeLegsError={routeLegsError}
                    session={session}
                    busy={busy}
                    setBusy={setBusy}
                    setNotice={setNotice}
                    refresh={refresh}
                  />
                )}

                {tab === 'photos' && (
                  <PhotoManager
                    trips={cloudTrips}
                    summits={summits}
                    routePoints={routePoints}
                    photos={photos}
                    photosReady={photosReady}
                    photosError={photosError}
                    session={session}
                    busy={busy}
                    setBusy={setBusy}
                    setNotice={setNotice}
                    refresh={refresh}
                  />
                )}
              </>
            )}
          </>
        )}

        {notice && <div className="admin-notice">{notice}</div>}
      </section>
    </div>
  )
}
